import styled from "styled-components";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { LoadingContainer } from "../../components/LoadingContainer";
import { Card } from "../../components/Card";
import { SALES_PIE_COLORS } from "../../utils/dailySalesUtils";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useTranslation } from "react-i18next";

const ChartCard = styled(Card)`
  padding: ${spacing.x2}px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 ${spacing.x2}px 0;
  font-size: ${fontSizes.large}px;
`;

const EmptyStateMessage = styled.div`
  padding: ${spacing.x4}px;
  text-align: center;
  color: ${colors.textMuted};
`;

const TooltipContent = styled.div`
  padding: ${spacing.x1}px;
  background: white;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${fontSizes.small}px;
`;

export interface SalesPieChartItem {
  key: string;
  name: string;
  value: number;
  label: string;
}

interface SalesPieChartCardProps {
  title: string;
  emptyMessage: string;
  isLoading: boolean;
  chartData: SalesPieChartItem[];
  showLegend?: boolean;
}

export const SalesPieChartCard = ({
  title,
  emptyMessage,
  isLoading,
  chartData,
  showLegend = true,
}: SalesPieChartCardProps) => {
  const { t } = useTranslation();

  if (isLoading && chartData.length === 0) {
    return (
      <ChartCard>
        <LoadingContainer message={t("statistics.loading")} />
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <ChartTitle>{title}</ChartTitle>
      {chartData.length === 0 ? (
        <EmptyStateMessage>{emptyMessage}</EmptyStateMessage>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={entry.key}
                  fill={SALES_PIE_COLORS[index % SALES_PIE_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) =>
                active && payload?.[0] ? (
                  <TooltipContent>
                    {payload[0].payload.name}: {payload[0].payload.label}
                  </TooltipContent>
                ) : null
              }
            />
            {showLegend && (
              <Legend wrapperStyle={{ fontSize: fontSizes.small }} />
            )}
          </PieChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};
